import { createFileRoute, redirect } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { getInvoiceForDownload } from "@/lib/documents.functions";
import { DocFooter, DocHeader, PrintActions, formatMoney, printStyles } from "@/components/documents/doc-shell";
import { Badge } from "@/components/ui/badge";

export const Route = createFileRoute("/invoice/$invoiceId")({
  beforeLoad: async () => {
    if (typeof window === "undefined") return;
    const { data, error } = await supabase.auth.getUser();
    if (error || !data.user) throw redirect({ to: "/login" });
  },
  head: () => ({ meta: [{ title: "Invoice" }] }),
  component: InvoiceDownloadPage,
});

function InvoiceDownloadPage() {
  const { invoiceId } = Route.useParams();
  const fetchInvoice = useServerFn(getInvoiceForDownload);
  const { data, isLoading, error } = useQuery({
    queryKey: ["invoice-download", invoiceId],
    queryFn: () => fetchInvoice({ data: { invoice_id: invoiceId } }),
  });

  if (isLoading) return <div className="p-10 text-center text-sm text-muted-foreground">Loading invoice…</div>;
  if (error || !data) {
    return <div className="p-10 text-center text-sm text-destructive">Unable to load invoice. {(error as Error)?.message}</div>;
  }

  const { invoice, items, branding } = data;
  const statusLabel = invoice.status.replace(/_/g, " ");
  const balance = invoice.total_cents - (invoice.amount_paid_cents ?? 0);

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-6 print:p-0">
      <style>{printStyles}</style>
      <PrintActions title={`Invoice ${invoice.number}`} />

      <div className="rounded-lg border bg-card p-8 shadow-sm print:border-0 print:shadow-none">
        <DocHeader
          branding={branding}
          title="INVOICE"
          subtitle={`# ${invoice.number}`}
        />

        <div className="mt-6 grid grid-cols-2 gap-6 text-sm">
          <div>
            <div className="text-xs font-semibold uppercase text-muted-foreground">Billed to</div>
            <div className="mt-1 font-medium">{invoice.customer_name ?? "—"}</div>
            {invoice.customer_email && <div className="text-muted-foreground">{invoice.customer_email}</div>}
            {invoice.customer_address && (
              <div className="text-muted-foreground whitespace-pre-line">{invoice.customer_address}</div>
            )}
          </div>
          <div className="text-right space-y-1">
            <div className="flex justify-end gap-3"><span className="text-muted-foreground">Status</span>
              <Badge variant={invoice.status === "paid" ? "default" : "secondary"} className="capitalize">{statusLabel}</Badge>
            </div>
            <div className="flex justify-end gap-3"><span className="text-muted-foreground">Issue date</span>
              <span>{invoice.issue_date}</span>
            </div>
            {invoice.due_date && (
              <div className="flex justify-end gap-3"><span className="text-muted-foreground">Due date</span><span>{invoice.due_date}</span></div>
            )}
            {invoice.paid_at && (
              <div className="flex justify-end gap-3"><span className="text-muted-foreground">Paid</span>
                <span>{new Date(invoice.paid_at).toLocaleDateString()}</span>
              </div>
            )}
          </div>
        </div>

        <table className="mt-8 w-full text-sm">
          <thead style={{ background: branding.accent_color + "15" }}>
            <tr>
              <th className="px-3 py-2 text-left">Description</th>
              <th className="px-3 py-2 text-right">Qty</th>
              <th className="px-3 py-2 text-right">Unit price</th>
              <th className="px-3 py-2 text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {items.map((i) => (
              <tr key={i.id} className="border-b">
                <td className="px-3 py-2">{i.description}</td>
                <td className="px-3 py-2 text-right">{i.qty}</td>
                <td className="px-3 py-2 text-right">{formatMoney(i.unit_price_cents, invoice.currency)}</td>
                <td className="px-3 py-2 text-right">{formatMoney(i.total_cents, invoice.currency)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="mt-6 flex justify-end">
          <div className="w-full max-w-xs space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{formatMoney(invoice.subtotal_cents, invoice.currency)}</span></div>
            {invoice.discount_cents > 0 && (
              <div className="flex justify-between"><span className="text-muted-foreground">Discount</span><span>− {formatMoney(invoice.discount_cents, invoice.currency)}</span></div>
            )}
            {invoice.tax_cents > 0 && (
              <div className="flex justify-between"><span className="text-muted-foreground">Tax</span><span>{formatMoney(invoice.tax_cents, invoice.currency)}</span></div>
            )}
            <div className="flex justify-between border-t pt-2 text-base font-semibold" style={{ borderColor: branding.accent_color }}>
              <span>Total</span><span>{formatMoney(invoice.total_cents, invoice.currency)}</span>
            </div>
            {invoice.amount_paid_cents > 0 && (
              <div className="flex justify-between text-muted-foreground"><span>Paid</span><span>{formatMoney(invoice.amount_paid_cents, invoice.currency)}</span></div>
            )}
            {balance > 0 && (
              <div className="flex justify-between font-medium"><span>Balance due</span><span>{formatMoney(balance, invoice.currency)}</span></div>
            )}
          </div>
        </div>

        {invoice.notes && (
          <div className="mt-6 rounded-md border bg-muted/30 p-3 text-sm">
            <div className="text-xs font-semibold uppercase text-muted-foreground mb-1">Notes</div>
            {invoice.notes}
          </div>
        )}

        <DocFooter branding={branding} />
      </div>
    </div>
  );
}
